import { EllipsisVertical } from "lucide-react";
import React, { memo, useMemo } from "react";
import { useImmer } from "use-immer";

import {
  IgAnalyticsTooltip,
  IgCheckbox,
  IgIconButton,
  IgToggleButtonGroup,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
} from "@intelligems/ig-types";
import { FormControlLabel, Menu, MenuItem, ToggleButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { GraphIntervalButtons } from "@src/features/analytics/baseDashboard/chartComponents/GraphIntervalButtons";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { FilterMethodHeaderDropdown } from "@src/features/analytics/baseDashboard/FilterMethodHeaderDropdown";
import { ProductDetailsTableTopNSelect } from "@src/features/analytics/baseDashboard/ProductDetailsTableTopNSelect";
import { ProductGroupBanner } from "@src/features/analytics/baseDashboard/ProductGroupBanner";
import { ProductGroupsTable } from "@src/features/analytics/tables/ProductGroupsTable";
import { ProductLevelDetailTable } from "@src/features/analytics/tables/productVariantsTable/ProductLevelDetailTable";
import { useDialogs } from "@src/hooks/dialog";
import { useDispatch, useSelector } from "@src/store";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

const tableFilterMap = {
  "Revenue per Unit": "revenue_per_unit",
  "Revenue per Visitor": "revenue_per_visitor",
  "Profit per Unit": "profit_per_unit",
  "Profit per Visitor": "profit_per_visitor",
  "Average Units Ordered": "units_per_order",
};

interface ProductGroupAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const ProductGroupAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: ProductGroupAnalyticsPageProps) => {
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const dashboardFilters = useSelector((state) => state.dashboardFilters);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    const [selectedFilterOption, setSelectedFilterOption] = useImmer<string>(
      Object.keys(tableFilterMap)[0]
    );
    const dispatch = useDispatch();

    const [variantSelection, setVariantSelection] =
      useImmer<string>("no_variants");

    const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);
    const { openDialog } = useDialogs();

    const [metricFilterIndex, setMetricFilterIndex] = useImmer(0);

    const hasProductGroups = useMemo(() => {
      if (data[0].isLoading) {
        return null;
      } else {
        return (data[0].data?.metrics || []).some(
          (metric) => metric.product_group !== "<No Product Group>"
        );
      }
    }, [data[0]]);

    const areProductsFetching = useAnalyticsFetching("variation_products");
    const areProductGroupsFetching = useAnalyticsFetching(
      "variation_product_groups"
    );

    const productVariationData = useMemo(() => {
      return (
        data
          .filter((dataSet) => dataSet.data?.datasetId === "variation_products")
          .map((dataSet) => {
            return dataSet.data?.metrics;
          })[0] ?? []
      );
    }, [data, dashboardFilters.showExperimentProductsOnly]);

    return (
      <Stack direction={"column"} rowGap={4} columnGap={2}>
        {data[0].isLoading ||
        areProductsFetching > 0 ||
        areProductGroupsFetching > 0 ||
        hasProductGroups === null ? null : (
          <ProductGroupBanner hasProductGroups={hasProductGroups} />
        )}
        {hasProductGroups === null ? <Grid /> : null}
        {hasProductGroups === false ? null : (
          <>
            <Grid size={12}>
              <BaseMetricSection
                groupTitle={graphConfigs[0].title[0]}
                tooltip={
                  "Your top product groups by revenue or units sold.  All remaining product groups are combined into the All Others group."
                }
                rightSideButtons={[
                  <IgAnalyticsTooltip
                    key={"filter-buttons"}
                    title={"Show product group mix by"}
                  >
                    <IgToggleButtonGroup
                      disabled={data[0].isLoading}
                      buttons={[
                        <ToggleButton
                          value="revenue"
                          key="revenue"
                          size={"small"}
                          sx={{
                            borderBottomLeftRadius: 9999,
                            borderTopLeftRadius: 9999,
                            paddingLeft: "12px",
                          }}
                        >
                          Revenue
                        </ToggleButton>,
                        <ToggleButton
                          value="units"
                          key="units"
                          size={"small"}
                          sx={{
                            borderBottomRightRadius: 9999,
                            borderTopRightRadius: 9999,
                            paddingRight: "12px",
                          }}
                        >
                          Units Sold
                        </ToggleButton>,
                      ]}
                      value={metricFilterIndex === 0 ? "revenue" : "units"}
                      control={{
                        exclusive: true,
                        onChange: (event, value: string) => {
                          if (value) {
                            setMetricFilterIndex(value === "revenue" ? 0 : 1);
                          }
                        },
                      }}
                    />
                  </IgAnalyticsTooltip>,
                ]}
              >
                <GraphsWrapper
                  totalTestVisitors={totalExperienceVisitors}
                  hideHeader={true}
                  config={graphConfigs[0]}
                  testDuration={testDuration}
                  renderLocation={"experiment"}
                  isDataLoading={data[0].isLoading || data[0].isPending}
                  data={selectData({
                    dashboardData: data,
                    config: graphConfigs[0],
                    _resource: experience,
                    locale: storeCurrencyConfig?.value?.locale,
                    metricFilterIndex,
                    variations,
                  })}
                  compareVariationId={compareVariationId}
                  rawData={data}
                />
              </BaseMetricSection>
            </Grid>
            <Grid size={12}>
              <BaseMetricSection
                headerDropdown={
                  <FilterMethodHeaderDropdown
                    selectedFilterOption={selectedFilterOption}
                    setSelectedFilterOption={setSelectedFilterOption}
                    filterOptions={Object.keys(tableFilterMap)}
                  />
                }
                groupTitle={"Product Group Detail"}
              >
                <ProductGroupsTable
                  selectedMetricId={tableFilterMap[selectedFilterOption]}
                  data={data}
                />
              </BaseMetricSection>
            </Grid>
          </>
        )}
        <Grid size={12}>
          <BaseMetricSection
            headerDropdown={<ProductDetailsTableTopNSelect />}
            rightSideButtons={[
              <IgAnalyticsTooltip
                key={"test-products-only"}
                title={"Filter to only products in the test"}
              >
                <FormControlLabel
                  key={"test-products-only"}
                  control={
                    <IgCheckbox
                      checked={!!dashboardFilters.showExperimentProductsOnly}
                      onChange={(_, checked) => {
                        dispatch(
                          updateDashboardFilter({
                            key: "showExperimentProductsOnly",
                            value: checked || null,
                          })
                        );
                      }}
                      size="small"
                    />
                  }
                  label="Test Products Only"
                />
              </IgAnalyticsTooltip>,
              <GraphIntervalButtons
                key={"filter-buttons"}
                graphInterval={variantSelection}
                hasNoData={false}
                setGraphInterval={(params) => {
                  const value = params !== "no_variants";
                  const key = "return_variants";
                  setVariantSelection(params);
                  dispatch(
                    updateDashboardFilter({
                      key,
                      value,
                    })
                  );
                }}
                isLoading={false}
                intervals={[
                  { label: "Product Level", value: "no_variants" },
                  { label: "Variant Level", value: "include_variants" },
                ]}
              />,
            ]}
            groupTitle={"Product-level Detail"}
          >
            <ProductLevelDetailTable
              variantSelection={variantSelection}
              data={productVariationData}
              isLoading={areProductsFetching > 0}
            />
          </BaseMetricSection>
        </Grid>
      </Stack>
    );
  }
);
